import React, { useMemo } from "react";
import { css } from "@emotion/react";
import Input from "components/atoms/Input";
import { Col, Row } from "react-flexbox-grid";
import { useTrans } from "system/translations/hooks";
import Divider from "components/atoms/Divider";
import Btn from "components/atoms/Btn";
import { DeleteIcon } from "evergreen-ui";
import themeOptions from "system/theme";
import { RoleWrapper } from "modules/admin/components/RoleWrapper";
import { createStyles } from "types/emotion-styles";
import { useDeleteMethodPaymentDetail } from "modules/merchantPaymentMethods/apiHooks";
import JsonOrInput from "components/molecules/JsonOrInput";

const styles = createStyles({
  notActiveWrap: {
    gap: "10px 0",
  },
  dividerStyles: {
    margin: "20px 0 0",
  },
  inputWithDeleteWrap: {
    display: "inline-flex",
    gap: themeOptions.spacing.xs,
    alignItems: "flex-end",
    "&>div": {
      width: "100%",
    },
  },
});

const DetailsItem = ({
  name,
  onChange,
  onBlur,
  value,
  onFormValueChange,
}: any) => {
  const { _t } = useTrans();
  const { mutateAsync, isLoading, variables } = useDeleteMethodPaymentDetail();
  const onRemove = async (id: number) => {
    await mutateAsync({ id });
    const newValues = value.filter((item: any) => item.id !== id);
    onFormValueChange(name, newValues);
  };

  const { editableInputs, editableInfo, disabled } = useMemo(() => {
    const result = {
      editableInputs: [] as any[],
      editableInfo: [] as any[],
      disabled: [] as any[],
    };

    value?.forEach((item: any, index: number) => {
      const isInput = item?.key?.toLowerCase().includes("input:text");

      if (!item?.enabled) {
        result.disabled.push({item, index});
      } else if (isInput) {
        result.editableInputs.push({item, index});
      } else {
        result.editableInfo.push({item, index});
      }
    });

    return result;
  }, [value]);

  const renderEditableRow = ({item, index}: any) => (
    <>
      <Col md={6}>
        <Input value={item?.key} label={_t("key")} disabled />
      </Col>
      <Col md={6}>
        <JsonOrInput
          name={`${name}[${index}].value`}
          value={item?.value}
          onBlur={() => onBlur(`${name}[${index}].value`)}
          onChange={(val: any) => onChange(val, `${name}[${index}].value`)}
          labelA={_t("value_json")}
          labelB={_t("value_input")}
        />
      </Col>
    </>
  );

  const renderDisabledRow = (item: any) => (
    <>
      <Col md={6}>
        <Input value={item?.key} label={_t("key")} disabled />
      </Col>
      <Col md={6} css={css(styles.inputWithDeleteWrap)}>
        <Input value={item?.value} label={_t("value")} disabled />
        <RoleWrapper accessTo="ROLE_DELETE_MERCHANT_METHOD_DETAILS">
          <Btn
            type="button"
            disabled={variables?.id === item?.id && isLoading}
            isLoading={variables?.id === item?.id && isLoading}
            hasIconOnly
            iconBefore={<DeleteIcon color={themeOptions.colors.red500} />}
            onClick={() => onRemove(item?.id)}
          />
        </RoleWrapper>
      </Col>
    </>
  );

  return (
    <>
      {editableInputs.length > 0 && (
        <>
          <Divider additionalClass={css(styles.dividerStyles)} text={_t("inputs_settings")} />
          <Row css={css(`gap: 10px 0`)}>
            {editableInputs.map(renderEditableRow)}
          </Row>
        </>
      )}

      {editableInfo.length > 0 && (
        <>
          <Divider additionalClass={css(styles.dividerStyles)} text={_t("info_list_settings")} />
          <Row css={css(`gap: 10px 0`)}>
            {editableInfo.map(renderEditableRow)}
          </Row>
        </>
      )}

      {disabled.length > 0 && (
        <>
          <Divider additionalClass={css(styles.dividerStyles)} text={_t("not_used_credentials")} />
          <Row css={css(styles.notActiveWrap)}>
            {disabled.map(renderDisabledRow)}
          </Row>
        </>
      )}
    </>
  );
};

export default DetailsItem;
